import { Button, Card, CardContent, Typography } from '@eye-able/ui';
import { AlertTriangle, ArrowLeft } from 'lucide-react';
import { useMemo, useState, type ReactNode } from 'react';
import {
  Navigate,
  Link as RouterLink,
  useNavigate,
  useParams,
} from 'react-router-dom';
import { isEqual } from 'lodash-es';
import { useFetchPolicy } from '@http/queries/useFetchPolicy';
import {
  useUpdatePolicy,
  type UpdatePolicyBody,
} from '@http/queries/useUpdatePolicy';
import LoadingSpinner from '@components/LoadingSpinner';
import ConfirmDialog from '@components/ConfirmDialog';
import PolicyForm, { policyToInitialValues } from './PolicyForm';
import { parsePolicyId } from './policyId';
import { useInlineTranslation } from '@/i18n/hooks/useInlineTranslation';
import { GovernanceAccessBoundary } from '../GovernanceAccessBoundary';
import { usePolicyRescan } from './usePolicyRescan';
import { useEditableGovernanceDomainOptions } from './governanceDomainOptions';
import { usePolicyEditAccess } from './usePolicyEditAccess';
import { T } from '@/i18n/T';

function EditPolicyStatusCard({ children }: { children: ReactNode }) {
  const { t } = useInlineTranslation();
  return (
    <div className='eye:flex eye:flex-col eye:gap-s'>
      <Button asChild variant='ghost' size='sm' className='eye:self-start'>
        <RouterLink to='/policies'>
          <ArrowLeft aria-hidden />
          {t('backToPolicies')}
        </RouterLink>
      </Button>
      <Card>
        <CardContent className='eye:p-s'>
          <Typography className='eye:text-grey-120'>{children}</Typography>
        </CardContent>
      </Card>
    </div>
  );
}

function EditPolicyContent() {
  const { t } = useInlineTranslation();
  const navigate = useNavigate();
  const { policyId } = useParams();
  const id = parsePolicyId(policyId);

  const { data: policy, isLoading, isError } = useFetchPolicy(id);
  const domainOptions = useEditableGovernanceDomainOptions(
    policy?.auditEntryIds ?? [],
  );
  const { canEditPolicy, isPending: isEditAccessPending } =
    usePolicyEditAccess();
  const updatePolicy = useUpdatePolicy();
  const { rescanPolicy } = usePolicyRescan(policy?.auditEntryIds ?? []);
  const [pendingRuleUpdate, setPendingRuleUpdate] =
    useState<UpdatePolicyBody | null>(null);

  const initialValues = useMemo(
    () => (policy ? policyToInitialValues(policy) : null),
    [policy],
  );

  if (id === null) {
    return (
      <EditPolicyStatusCard>
        <T k='policyNotFound' />
      </EditPolicyStatusCard>
    );
  }

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (isError || !policy || !initialValues) {
    return (
      <EditPolicyStatusCard>
        <T k='couldNotLoadPolicy' />
      </EditPolicyStatusCard>
    );
  }

  // The boundary above only proves the user may see policies; editing this one
  // needs editor access to every domain it covers, as `policies PUT` checks.
  if (isEditAccessPending) {
    return <LoadingSpinner />;
  }

  if (!canEditPolicy(policy)) {
    return <Navigate to='/policies' replace />;
  }

  const savePolicy = (body: UpdatePolicyBody) => {
    if (!isEqual(policy.rule, body.rule)) {
      setPendingRuleUpdate(body);
      return;
    }

    void updatePolicy.mutate(body, {
      onSuccess: (updatedPolicy) => {
        void rescanPolicy(updatedPolicy, { showSuccessNotification: false });
        void navigate('/policies');
      },
    });
  };

  const confirmRuleUpdate = () => {
    if (!pendingRuleUpdate) return;

    void updatePolicy.mutate(pendingRuleUpdate, {
      onSuccess: (updatedPolicy) => {
        void rescanPolicy(updatedPolicy, { showSuccessNotification: false });
        void navigate('/policies');
      },
    });
  };

  return (
    <>
      <PolicyForm
        heading={t('editPolicy')}
        description={t('editPolicyDescription')}
        submitLabel={t('saveChanges')}
        canSaveAsDraft={false}
        isSubmitting={updatePolicy.isPending}
        initialValues={initialValues}
        domainOptions={domainOptions}
        onSubmit={(body) => savePolicy({ ...body, id })}
      />
      <ConfirmDialog
        open={pendingRuleUpdate !== null}
        onOpenChange={(open) => {
          if (!open) setPendingRuleUpdate(null);
        }}
        title={t('updatePolicyRule')}
        description={t('updatePolicyRuleConfirmation')}
        confirmText={t('updateRule')}
        confirmIcon={<AlertTriangle aria-hidden />}
        loading={updatePolicy.isPending}
        onConfirm={confirmRuleUpdate}
      />
    </>
  );
}

function EditPolicy() {
  return (
    <GovernanceAccessBoundary>
      <EditPolicyContent />
    </GovernanceAccessBoundary>
  );
}

export { EditPolicy as Component };
export default EditPolicy;
